import React, { useMemo, useRef } from "react";
import { Container, Graphics, Sprite } from "@pixi/react";
import { useDispatch } from "react-redux";
import { usePrimaryUIToolbarDimensionsV2 } from "../../../../Hooks/usePrimaryUIToolbarDimensionsV2";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { PixiSettings } from "../../../../pixiSettings";
import {
  PRIMARY_USER_INTERFACE_BUTTON_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
} from "../../../../textureConfigs";
import { getUITexture } from "../../../../assets";
import { closeMenu } from "../../../../State/actions";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
import ButtonGameRules from "../Buttons/ButtonGameRules";
import ButtonPaytable from "../Buttons/ButtonPaytable";
import ButtonSettings from "../Buttons/ButtonSettings";

// Full opacity background for menu toolbar
const TOOLBAR_BACKGROUND_COLOR = 0x090f41;
const TOOLBAR_BACKGROUND_OPACITY = 1.0;

const MenuToolbarBottom = () => {
  const dispatch = useDispatch();
  const toolbar = usePrimaryUIToolbarDimensionsV2();
  const { dimensions, device, orientation, getResponsiveConfig } =
    useDimensions();
  const {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  } = useButtonHover();
  const isProcessingClickRef = useRef(false);
  const debounceTimeoutRef = useRef(null);

  const isMobilePortrait =
    device === PixiSettings.DEVICE.MOBILE &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const bottomY = isMobilePortrait
    ? dimensions.height - toolbar.height
    : dimensions.height - toolbar.height;

  const texture = getUITexture("icon_menu_active");

  // Positioning config — same as ToolbarBottom uses for menu button position
  const positionButtonConfig = useMemo(
    () => getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  // Sprite sizing config — same as ButtonMenu uses for icon size
  const spriteButtonConfig = useMemo(
    () => getResponsiveConfig(PRIMARY_USER_INTERFACE_BUTTON_CONFIG, true),
    [getResponsiveConfig],
  );

  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;

  // Exact same positioning logic as ToolbarBottom + ButtonMenu
  const menuPosition = useMemo(() => {
    if (isDesktop) {
      // Desktop: left side — ToolbarBottom overrides with USER_INTERFACE_BUTTON_CONFIG
      return {
        x: positionButtonConfig.width * 0.8,
        y: toolbar.height / 2,
      };
    }
    // Mobile/Tablet: right side — ButtonMenu uses PRIMARY_USER_INTERFACE_BUTTON_CONFIG internally
    return {
      x: toolbar.width - spriteButtonConfig.width * 1.0,
      y: toolbar.height / 2,
    };
  }, [isDesktop, positionButtonConfig.width, spriteButtonConfig.width, toolbar.width, toolbar.height]);

  const handleClick = () => {
    if (isProcessingClickRef.current) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;
    soundManager.play(SOUND_NAME.button_click);
    triggerPress();

    // Delay close so the press animation (80ms + 180ms) plays fully before the overlay fades out
    setTimeout(() => {
      dispatch(closeMenu());
    }, 260);

    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  return (
    <Container
      name="menu_toolbar_bottom"
      x={(dimensions.width - toolbar.width) / 2}
      y={bottomY}
      sortableChildren
    >
      {/* Full-width shadow above toolbar */}
      <Graphics
        name="menu_toolbar_bottom_shadow"
        draw={(g) => {
          g.clear();
          const offset = (dimensions.width - toolbar.width) / 2;
          const shadowHeight = 50;
          for (let i = 0; i < shadowHeight; i++) {
            const t = 1 - i / shadowHeight;
            const alpha = t * t * 0.9;
            g.beginFill(0x060b30, alpha);
            g.drawRect(-offset, -i - 1, dimensions.width, 1);
            g.endFill();
          }
        }}
      />

      {/* Full-width background */}
      <Graphics
        name="menu_toolbar_bottom_background"
        draw={(g) => {
          g.clear();
          const offset = (dimensions.width - toolbar.width) / 2;
          g.beginFill(TOOLBAR_BACKGROUND_COLOR, TOOLBAR_BACKGROUND_OPACITY);
          g.drawRect(-offset, 0, dimensions.width, toolbar.height);
          g.endFill();
        }}
      />

      {/* Menu button (yellow active icon) — same position as regular toolbar */}
      <Container
        name="menu_close_button"
        x={menuPosition.x}
        y={menuPosition.y}
        zIndex={2}
      >
        <Container ref={hoverContainerRef}>
          <Sprite
            ref={buttonRef}
            texture={texture}
            name="button_menu_active_icon"
            anchor={0.5}
            width={spriteButtonConfig.width}
            height={spriteButtonConfig.height}
            x={0}
            y={0}
            eventMode="static"
            cursor="pointer"
            onpointertap={handleClick}
            onpointerover={handlePointerOver}
            onpointerout={handlePointerOut}
            resolution={2}
          />
        </Container>
      </Container>

      {/* Section buttons: Rules | Paytable | Settings */}
      <ButtonGameRules toolbar={toolbar} />
      <ButtonPaytable toolbar={toolbar} />
      <ButtonSettings toolbar={toolbar} />
    </Container>
  );
};

export default MenuToolbarBottom;
